import { BodyText, Strong } from '@utilitywarehouse/hearth-react';

import {
  EnergyFuelType,
  EnergyMeterType,
  EnergyQuoteDetails,
  PredefinedConsumption,
} from '@generated/gql/graphql';
import { formatNumber } from '@utils/format';

import { hasServices } from '../utils';

interface UsageRowProps {
  details: EnergyQuoteDetails;
  fuelType: EnergyFuelType;
}

const UsageRow = ({ details, fuelType }: UsageRowProps) => {
  const {
    gasUsage,
    electricityUsageDay,
    electricityUsageNight,
    meterType,
    predefinedConsumption,
  } = details;

  const hasStandardUsage = ![
    PredefinedConsumption.Custom,
    PredefinedConsumption.Prefilled,
  ].includes(predefinedConsumption);

  const renderContent = () => {
    if (hasStandardUsage) {
      return (
        <BodyText>
          My energy usage is{' '}
          <Strong>{predefinedConsumption.toLowerCase()}</Strong>
        </BodyText>
      );
    }

    const gasAmount = formatNumber(gasUsage);
    const electricityDayAmount = formatNumber(electricityUsageDay);
    const electricityNightAmount = formatNumber(electricityUsageNight ?? 0);
    const { hasElectricity, hasGas } = hasServices(fuelType);
    const isEcon7 = meterType === EnergyMeterType.Economy_7;

    if (!hasElectricity) {
      return (
        <BodyText>
          I use approximately <Strong>{gasAmount} kWh</Strong> of gas each year
        </BodyText>
      );
    }

    if (!hasGas) {
      if (isEcon7) {
        return (
          <BodyText>
            I use approximately{' '}
            <Strong>
              {electricityDayAmount}/{electricityNightAmount} kWh
            </Strong>{' '}
            (day/night) of electricity each year
          </BodyText>
        );
      }

      return (
        <BodyText>
          I use approximately <Strong>{electricityDayAmount} kWh</Strong> of
          electricity each year
        </BodyText>
      );
    }

    if (isEcon7) {
      return (
        <BodyText>
          I use approximately{' '}
          <Strong>
            {electricityDayAmount}/{electricityNightAmount} kWh
          </Strong>{' '}
          (day/night) of electricity and <Strong>{gasAmount} kWh</Strong> of gas
          each year
        </BodyText>
      );
    }

    return (
      <BodyText>
        I use approximately <Strong>{electricityDayAmount} kWh</Strong> of
        electricity and <Strong>{gasAmount} kWh</Strong> of gas each year
      </BodyText>
    );
  };

  return <li>{renderContent()}</li>;
};

export { UsageRow };
